import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import {
  getProjectsAPI,
  getProjectDetailsAPI,
  createProjectTableAPI,
  updateTableNameAPI,
  deleteProjectTableAPI,
  cloneTableFromTemplateAPI,
  getProjectTableAPI,
  getChatSessionAPI,
  createChatSessionAPI,
  getProfileAPI
} from "../utils/api";
import ConfirmationDialog from "./ConfirmationDialog";
import PopupErrors        from "./PopupErrors";
/* ╭────────────────────────────────────────────────────────────╮
   │ 1) CONTEXTO para navegación / resaltado en el visor PDF   │
   ╰────────────────────────────────────────────────────────────╯ */
export const PdfHighlightContext = createContext({
  task: null,      // { page, rects[] } | null
  goTo: () => {},  // (page, rects[]) => void
  clear: () => {}, // () => void
});

export function PdfHighlightProvider({ children }) {
  const [task, setTask] = useState(null);
  const goTo  = useCallback((page, rects = []) => setTask({ page, rects }), []);
  const clear = useCallback(() => setTask(null), []);
  return (
    <PdfHighlightContext.Provider value={{ task, goTo, clear }}>
      {children}
    </PdfHighlightContext.Provider>
  );
}

/* ╭────────────────────────────────────────────────────────────╮
   │ 2) CONTEXTO y proveedor principal de proyectos            │
   ╰────────────────────────────────────────────────────────────╯ */
export const ProjectContext = createContext();

/* ---------- Helpers API → UI ---------------------------------------- */
// ProjectProvider.js  →  mapApiRow corregido
const mapApiRow = (r) => ({
  id        : r.id,
  groupName : r.group_name,

  // ⬇️  SIN .trim(): los espacios se conservan
  stringKeywords: (r.criteria?.string_match_keywords || [])
    .flatMap(kw => kw.split(","))   // compatibilidad con datos antiguos
    .filter(Boolean)                // descarta solo cadenas vacías
    .join("\n"),

  vectorKeywords: (r.criteria?.vector_match_keywords || [])
    .flatMap(kw => kw.split(","))
    .filter(Boolean)
    .join("\n"),

  summaryInstructions: r.criteria?.summary_instructions ?? "",
  extraction         : r.summary,
  chunk_coordinates  : r.chunk_coordinates ?? {},
});


const mapApiTable = (t) => ({
  id               : t.id,
  name             : t.name,
  extractionStatus : t.extraction_status ?? "never-instantiated",
  rows             : t.rows.map(mapApiRow),
});

/* ---------- PROVIDER ------------------------------------------------- */
const ProjectProvider = ({ children }) => {
  /* Perfil de usuario ----------------------------------------------- */
  const [profile, setProfile] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const data = await getProfileAPI();
        setProfile(data);
      } catch (e) {
        console.error("No se pudo cargar el perfil:", e);
      }
    })();
  }, []);

  /* Pane states ------------------------------------------------------- */
  const [isProjectListCollapsed, setProjectListCollapsed] = useState(false);
  const toggleProjectList = () => setProjectListCollapsed(p => !p);
  const [isCenterCollapsed, setCenterCollapsed] = useState(true);

  /* Projects list ----------------------------------------------------- */
  const [projects, setProjects]               = useState([]);
  const [hasMoreProjects, setHasMoreProjects] = useState(false);
  const [isLoadingMoreProjects, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);   // 🆕 nuevo estado

  /* Current project --------------------------------------------------- */
  const [selectedProject, setSelectedProject] = useState(null);
  const [currentProject , setCurrentProject ] = useState(null);

  useEffect(() => {
    setCenterCollapsed(!selectedProject);
  }, [selectedProject]);

  /* PDF loading flag -------------------------------------------------- */
  const [isPdfLoading, setPdfLoading] = useState(false);
  /* PDF uploading flag (subida) -------------------------------------------------- */
  const [isUploadingPdf, setUploadingPdf] = useState(false);

  /* Tables ------------------------------------------------------------ */
  const [tables,        setTables]        = useState([]);
  const [activeTableId, setActiveTableId] = useState(null);

  /* Chat -------------------------------------------------------------- */
  const [chatSessionsByProject, setChatSessionsByProject] = useState({
    id: "",
    messages: { data: [] },
    thread_id: "",
  });
  const [chatSessionIsLoading, setChatSessionIsLoading] = useState(false);
  /*.......*/
  const [loadingActions, setLoadingActions] = useState({});

  /* ---------- utils -------------------------------------------------- */
  const resetTables = useCallback(() => {
    setTables([]);
    setActiveTableId(null);
  }, []);

  const reloadChatSession = async (projectId, chatSessionId) => {
    if (!chatSessionId) return;
    setChatSessionIsLoading(true);
    const session = await getChatSessionAPI(projectId, chatSessionId, 20);
    setChatSessionsByProject(session.chat_session);
    setChatSessionIsLoading(false);
  };

  /* ---------- load one project + tables ----------------------------- */
  const loadProjectDetails = useCallback(
    async (projectId) => {
      resetTables();
      setPdfLoading(true); // comienza descarga PDF

      const { project } = await getProjectDetailsAPI(projectId);

      setCurrentProject(project);
      setSelectedProject({
        id        : project.id,
        name      : project.name,
        created_at: project.created_at,
      });

      const mappedTables = project.tables.map(mapApiTable);
      setTables(mappedTables);
      setActiveTableId(mappedTables[0]?.id ?? null);

      const lastSession = project.chat_sessions.at(-1);
      await reloadChatSession(project.id, lastSession?.id);

      setPdfLoading(false); // fin descarga PDF
      return project;
    },
    [resetTables]
  );

  /* ---------- initial load of projects list ------------------------- */
  useEffect(() => {
    (async () => {
      setIsInitialLoading(true);                 // 🆕 empieza la carga
      try {
        const data = await getProjectsAPI(20);
        setProjects(data.projects);
        setHasMoreProjects(data.has_more_projects);
      } catch (err) {
        console.error("Error cargando proyectos:", err);
      } finally {
        setIsInitialLoading(false);              // 🆕 finaliza
      }
    })();
  }, []);

  const loadMoreProjects = async () => {
    if (isLoadingMoreProjects || !hasMoreProjects) return;
    setIsLoading(true);
    const last = projects.at(-1);
    const data = await getProjectsAPI(20, last?.id);
    setProjects(p => [...p, ...data.projects]);
    setHasMoreProjects(data.has_more_projects);
    setIsLoading(false);
  };

  /* ---------- tables CRUD (add/rename/remove/refresh) --------------- */
  const addTable = useCallback(async () => {
    if (!selectedProject) return;
    const defaultName = 'Table ' + (tables.length + 1);
    const newApiTable = await createProjectTableAPI(selectedProject.id, {
      name: defaultName,
    });
    const newTable = {
      id              : newApiTable.id,
      name            : newApiTable.name,
      extractionStatus: newApiTable.extraction_status ?? "never-instantiated",
      rows            : [],
    };
    setTables(p => [...p, newTable]);
    setActiveTableId(newTable.id);
  }, [selectedProject, tables]);

  const addTableFromTemplate = useCallback(
    async (templateId, templateName) => {
      if (!selectedProject) return;
      const apiTable = await cloneTableFromTemplateAPI(
        selectedProject.id,
        templateId,
        templateName,
      );
      const newTable = mapApiTable(apiTable);
      setTables(p => [...p, newTable]);
      setActiveTableId(newTable.id);
    },
    [selectedProject]
  );

  const renameTable = useCallback(
    async (tableId, newName) => {
      if (!selectedProject) return;
      await updateTableNameAPI(selectedProject.id, tableId, newName);
      setTables(p =>
        p.map(t => t.id === tableId ? { ...t, name: newName } : t)
      );
    },
    [selectedProject]
  );

  const removeTable = useCallback(
    async (tableId) => {
      if (!selectedProject) return;
      await deleteProjectTableAPI(selectedProject.id, tableId);
      setTables(p => p.filter(t => t.id !== tableId));
      if (activeTableId === tableId) setActiveTableId(null);
    },
    [selectedProject, activeTableId]
  );

  const refreshTable = useCallback(
    async (tableId) => {
      if (!selectedProject) return;
      const apiTable = await getProjectTableAPI(selectedProject.id, tableId);
      const uiTable  = mapApiTable(apiTable);
      setTables(p => p.map(t => t.id === tableId ? uiTable : t));
    },
    [selectedProject]
  );

  /* ---------- chat sessions ----------------------------------------- */
  const createNewChatSession = useCallback(async () => {
    if (!selectedProject) return;
    setChatSessionIsLoading(true);
    const session = await createChatSessionAPI(selectedProject.id);
    setChatSessionsByProject(session.chat_session);
    setChatSessionIsLoading(false);
  }, [selectedProject]);

  /* ---------- confirmation dialog ----------------------------------- */
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [resolveConfirm, setResolveConfirm] = useState(null);

  const confirm = useCallback(({ title, message }) => {
    return new Promise((resolve) => {
      setConfirmDialog({ title, message });
      setResolveConfirm(() => resolve);
    });
  }, []);

  const handleConfirmYes = () => {
    setConfirmDialog(null);
    if (resolveConfirm) resolveConfirm(true);
  };

  const handleConfirmNo = () => {
    setConfirmDialog(null);
    if (resolveConfirm) resolveConfirm(false);
  };

  /*para mandar el texto de la tabla al input de chatbar*/
  const [presetChatInput, setPresetChatInput] = useState("");

  /*para ocultar left-header y pdf-canvas-wrapper en column-right*/
  const [isPdfColumnCollapsed, setPdfColumnCollapsed] = useState(false);

  // popup de errores (tokens agotados, cuotas, etc.)
  const [errorPopup, setErrorPopup] = useState(null);

  const openErrorPopup  = ({ msg, showBuy = false }) =>
    setErrorPopup({ msg, showBuy });

  const closeErrorPopup = () => setErrorPopup(null);


  /* ---------- context value ----------------------------------------- */
  const ctxValue = {
    /* panes */
    isProjectListCollapsed,
    toggleProjectList,
    isCenterCollapsed,
    setCenterCollapsed,

    /* projects list */
    projects,
    setProjects,
    hasMoreProjects,
    loadMoreProjects,
    isLoadingMoreProjects,
    isInitialLoading,    // 🆕

    /* current project & tables */
    selectedProject,
    setSelectedProject,
    currentProject,
    setCurrentProject,
    loadProjectDetails,

    /* PDF loading flag */
    isPdfLoading,
    setPdfLoading,

    /* tables */
    tables,
    setTables,
    activeTableId,
    setActiveTableId,
    addTable,
    addTableFromTemplate,
    renameTable,
    removeTable,
    refreshTable,

    /* perfil */
    profile,

    /* chat */
    chatSessionsByProject,
    setChatSessionsByProject,
    reloadChatSession,
    chatSessionIsLoading,
    setChatSessionIsLoading,
    createNewChatSession,

    confirm,

    loadingActions,
    setLoadingActions,

    presetChatInput,
    setPresetChatInput,

    isPdfColumnCollapsed,
    setPdfColumnCollapsed,

    /* upload flag */
    isUploadingPdf,
    setUploadingPdf,

    errorPopup,
    openErrorPopup,
    closeErrorPopup,

  };

  return (
    <ProjectContext.Provider value={ctxValue}>
      {children}

      {/* Popup global de errores */}
      <PopupErrors />

      {confirmDialog && (
        <ConfirmationDialog
          title={confirmDialog.title}
          message={confirmDialog.message}
          onConfirm={handleConfirmYes}
          onCancel={handleConfirmNo}
        />
      )}
    </ProjectContext.Provider>
  );
};



/* ---------- EXPORTS -------------------------------------------------- */
export { ProjectProvider };
export default ProjectProvider;
